import { useMemo } from 'react';
import useEnvList from './useEnvList';
import {
  AVAILABLE_REQUESTING_PLATFORMS_ENV_VARIABLE_NAME,
  ACTIVE_REQUESTING_PLATFORM_ENV_VARIABLE_NAME,
} from '../constants/requesting-platform';
import {
  getPlatformFeatures,
  isPlatformFeatureEnabled,
  getFeatureValue as getFeatureValueUtil,
} from '../constants/platform-features';

/**
 * Hook to access platform-specific features
 *
 * This hook reads the available platforms from the environment variable
 * and provides utilities to check if features are enabled for those platforms.
 *
 * Features are considered enabled if they are enabled for ANY of the available platforms.
 * This allows multi-platform configurations (e.g., SANTANDER,FINGO).
 *
 * Non-boolean feature values are resolved against REACT_APP_ACTIVE_REQUESTING_PLATFORM.
 *
 * @example
 * const { isFeatureEnabled, availablePlatforms, getFeatureConfig,
 *  getFeatureValue } = usePlatformFeatures();
 *
 * // Check if a feature is enabled for any available platform
 * if (isFeatureEnabled('login.sso.entraId')) {
 *   return <EntraIdSignInButton />;
 * }
 *
 * // Get all enabled features for a specific platform
 * const bchFeatures = getFeatureConfig('BCH');
 *
 * // Get a non-boolean feature value (e.g. visible column field names)
 * const visibleColumns = getFeatureValue('pricing.monthlyRanges.visibleColumns');
 *
 * @returns {Object} Object containing:
 *   - isFeatureEnabled: Function to check if a feature is enabled
 *   - availablePlatforms: Array of available platform codes
 *   - getFeatureConfig: Function to get all features for a platform
 *   - getFeatureValue: Function to get a feature's raw value for the active platform
 *   - hasAnyPlatform: Function to check if any of the specified platforms are available
 */
const usePlatformFeatures = () => {
  const availablePlatforms = useEnvList(AVAILABLE_REQUESTING_PLATFORMS_ENV_VARIABLE_NAME, ',', []);
  const activePlatform = process.env[ACTIVE_REQUESTING_PLATFORM_ENV_VARIABLE_NAME];

  const isFeatureEnabled = useMemo(() => (featureKey) => {
    // If no platforms are configured, no features are enabled
    if (!availablePlatforms || availablePlatforms.length === 0) {
      return false;
    }

    // A feature is enabled if it's enabled for ANY of the available platforms
    return availablePlatforms.some((
      platformCode,
    ) => isPlatformFeatureEnabled(platformCode, featureKey));
  }, [availablePlatforms]);

  const getFeatureConfig = useMemo(() => (platformCode) => getPlatformFeatures(platformCode), []);

  const getFeatureValue = useMemo(() => (featureKey, defaultValue) => {
    if (!activePlatform) {
      return defaultValue;
    }

    const value = getFeatureValueUtil(activePlatform, featureKey);
    return value !== null && value !== undefined ? value : defaultValue;
  }, [activePlatform]);

  const hasAnyPlatform = useMemo(() => (platformCodes) => {
    if (!availablePlatforms || availablePlatforms.length === 0) {
      return false;
    }

    const platformCodesArray = Array.isArray(platformCodes)
      ? platformCodes
      : [platformCodes];

    return platformCodesArray.some((code) => availablePlatforms.includes(code));
  }, [availablePlatforms]);

  return {
    isFeatureEnabled,
    availablePlatforms,
    getFeatureConfig,
    getFeatureValue,
    hasAnyPlatform,
  };
};

export default usePlatformFeatures;
